import { createContext } from 'react';
import { type FieldValues, type UseFormReturn } from 'react-hook-form';

import type * as QuestionnaireApiModels from '@/typings/questionnaires-api-models';

import { type HandleAutoSaveV2Controls } from '../../form/HandleWatchers/HandleAutoSave/typings';

export interface AccumulatedModalChanges {
  values: Map<string, unknown>;
  responseSources: Map<string, QuestionnaireApiModels.QuestionnaireResponse['source']>;
  responseSourceIds: Map<string, QuestionnaireApiModels.QuestionnaireResponse['sourceId']>;
  aiRequestIds: Map<string, string | null>;
  autofillStatuses: Map<string, boolean>;
  explanationHtmls: Map<string, string | null>;
}

export type QuestionnaireFormContextType = {
  formContextId: string;
  formMethods: UseFormReturn<FieldValues>;
  getExplanationHtml: (fieldKey: string) => string | null;
  setExplanationHtml: (fieldKey: string, explanationHtml: string | null) => void;
  getResponseSource: (fieldKey: string) => QuestionnaireApiModels.QuestionnaireResponse['source'];
  setResponseSource: (fieldKey: string, source: QuestionnaireApiModels.QuestionnaireResponse['source']) => void;
  getBulkResponseSources: (args?: {
    rowFieldKey: string;
  }) => Record<string, QuestionnaireApiModels.QuestionnaireResponse['source']>;
  mergeMetadataFromContext: (context: QuestionnaireFormContextType) => void;
  mergeResponseSources: (source: Record<string, QuestionnaireApiModels.QuestionnaireResponse['source']>) => void;
  getAiRequestId: (fieldKey: string) => string | null;
  setAiRequestId: (fieldKey: string, aiRequestId: string | null) => void;
  getAutofillStatus: (fieldKey: string) => boolean;
  setAutofillStatus: (fieldKey: string, status: boolean) => void;
  getBulkAutofillStatuses: (args?: { rowFieldKey: string }) => Record<string, boolean>;
  mergeAutofillStatuses: (statuses: Record<string, boolean>) => void;
  getBulkAiRequestIds: (args?: { rowFieldKey: string }) => Record<string, string | null>;
  getResponseSourceId: (fieldKey: string) => QuestionnaireApiModels.QuestionnaireResponse['sourceId'];
  setResponseSourceId: (fieldKey: string, source: QuestionnaireApiModels.QuestionnaireResponse['sourceId']) => void;
  getListRowIdFromSourceId: (sourceId: string) => string | null;
  getResponseSourceIndex: (fieldKey: string) => QuestionnaireApiModels.QuestionnaireResponse['sourceIndex'];
  setResponseSourceIndex: (
    fieldKey: string,
    sourceIndex: QuestionnaireApiModels.QuestionnaireResponse['sourceIndex'],
  ) => void;
  mergeAiRequestIds: (source: Record<string, string | null>) => void;
  setValue: (arg1: {
    key: string;
    value: unknown;
    source?: QuestionnaireApiModels.QuestionnaireResponse['source'];
    sourceId?: QuestionnaireApiModels.QuestionnaireResponse['sourceId'];
    aiRequestId?: string | null;
    explanationHtml?: string | null;
    hasAutofillRun?: boolean;
  }) => void;
  completionRequiresAllRequiredFields: boolean;
  isModalContext: boolean;
  modalLevel: number;
  getAccumulatedModalChanges: () => AccumulatedModalChanges;
  applyNestedModalChanges: (changes: AccumulatedModalChanges) => void;
  setCanSaveValueToDB: (value: boolean) => void;
  getCanSaveValueToDB: () => boolean;
  removeRowMetaData: (listFieldKey: string, removedIndex: number) => void;
  clearListMetaData: (listFieldKey: string) => void;
  autoSave: HandleAutoSaveV2Controls;
};

const unimplementedFunction: any = (): void => {
  throw new Error('Function not implemented.');
};

export const defaultAutoSave: HandleAutoSaveV2Controls = {
  add: unimplementedFunction,
  forceSave: unimplementedFunction,
  pause: unimplementedFunction,
  play: unimplementedFunction,
};

const QuestionnaireFormContext = createContext<QuestionnaireFormContextType>({
  applyNestedModalChanges: unimplementedFunction,
  autoSave: defaultAutoSave,
  completionRequiresAllRequiredFields: false,
  clearListMetaData: unimplementedFunction,
  formContextId: 'default',
  formMethods: {} as any,
  getAccumulatedModalChanges: unimplementedFunction,
  getAiRequestId: unimplementedFunction,
  getAutofillStatus: unimplementedFunction,
  getBulkAiRequestIds: unimplementedFunction,
  getBulkAutofillStatuses: unimplementedFunction,
  getBulkResponseSources: unimplementedFunction,
  getCanSaveValueToDB: unimplementedFunction,
  getExplanationHtml: unimplementedFunction,
  getListRowIdFromSourceId: unimplementedFunction,
  getResponseSource: unimplementedFunction,
  getResponseSourceId: unimplementedFunction,
  getResponseSourceIndex: unimplementedFunction,
  isModalContext: false,
  mergeAiRequestIds: unimplementedFunction,
  mergeAutofillStatuses: unimplementedFunction,
  mergeMetadataFromContext: unimplementedFunction,
  mergeResponseSources: unimplementedFunction,
  modalLevel: 0,
  removeRowMetaData: unimplementedFunction,
  setAiRequestId: unimplementedFunction,
  setAutofillStatus: unimplementedFunction,
  setCanSaveValueToDB: unimplementedFunction,
  setExplanationHtml: unimplementedFunction,
  setResponseSource: unimplementedFunction,
  setResponseSourceId: unimplementedFunction,
  setResponseSourceIndex: unimplementedFunction,
  setValue: unimplementedFunction,
});

export default QuestionnaireFormContext;
